import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatINR } from '../../lib/quote';
import { IMG_FALLBACK } from '../../lib/images';
import { addToCart, openCart } from '../../lib/cart';
import { track, FUNNEL } from '../../lib/track';
import { tableSections, unitName, kindText } from '../../data/compare';
import { COMPARE_CTX } from '../../lib/compareTray';

// The full spec sheet, CarDekho-style: sections you can fold, a "Show only
// differences" switch, Yes / No marks for features, and a green tick on the
// better value where "better" is clear. On a phone two columns show at a
// time and the header (photo, name, price, pipe-kit switch) stays stuck under
// the site header while the rows scroll; swipe or use the arrows for the
// rest. No scroll container is used, because a sticky header cannot pin to
// the page from inside one.

const useIsPhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !window.matchMedia('(min-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const fn = () => setPhone(!mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return phone;
};

const productHref = (u) => (u.clearance ? `/${u.model.slug}` : `/${u.model.slug}?kit=${u.kit}`);

function KitSwitch({ u, onKit }) {
  if (!u.kits || u.kits.length < 2) {
    return <p className="text-[10px] text-gray-500 sm:text-[11px]">{u.kit === 'withKit' ? 'Pipe kit included' : 'No pipe kit'}</p>;
  }
  return (
    <div className="mt-1 flex flex-wrap justify-center gap-1" role="group" aria-label={`Pipe kit for ${unitName(u)}`}>
      {u.kits.map((k) => {
        const on = k.kit === u.kit;
        return (
          <button key={k.kit} type="button" aria-pressed={on} onClick={() => !on && onKit(u.id, k.id)}
            data-testid={`kit-${u.modelId}-${k.kit}`}
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold sm:text-[11px] ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400'}`}>
            {k.kit === 'withKit' ? 'With kit' : 'Without kit'}
          </button>
        );
      })}
    </div>
  );
}

function Head({ u, onKit }) {
  return (
    <div className="flex min-w-0 flex-col items-center px-1 pb-2 pt-2 text-center">
      {u.clearance && (
        <span className="mb-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Clearance · {u.model.year} model</span>
      )}
      <img src={u.model.image} alt={u.model.title} width="96" height="96" loading="lazy"
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
        className={`h-14 w-full object-contain sm:h-20 ${u.soldOut ? 'opacity-40' : ''}`} />
      <p className="mt-1 line-clamp-2 text-xs font-bold leading-tight text-gray-900 sm:text-sm">{unitName(u)}</p>
      <p className="text-[11px] leading-tight text-gray-600">{kindText(u)}</p>
      <p className="text-sm font-extrabold text-gray-900 sm:text-base" data-testid={`col-price-${u.id}`}>{formatINR(u.price)}</p>
      {u.mrp && u.mrp > u.price && <p className="text-[10px] text-gray-500 line-through">MRP {formatINR(u.mrp)}</p>}
      {u.soldOut && <p className="text-[10px] font-semibold text-red-600">Sold out</p>}
      <KitSwitch u={u} onKit={onKit} />
    </div>
  );
}

function Foot({ u }) {
  const buy = () => {
    if (!u.inStock) { window.location.assign(`/request-unit?model=${u.model.slug}&kit=${u.kit}`); return; }
    addToCart(u.model.id, u.kit, 1);
    track(FUNNEL.ADD_TO_CART, { model: u.model.id, kit: u.kit, qty: 1, value: u.price, source: 'compare' });
    openCart();
  };
  return (
    <div className="flex flex-col gap-1.5 p-1.5">
      <button type="button" onClick={buy} data-testid={`col-buy-${u.id}`}
        className="h-9 rounded-md bg-blue-600 text-xs font-semibold text-white hover:bg-blue-700">
        {u.inStock ? 'Add to cart' : 'Request this AC'}
      </button>
      <Link to={productHref(u)} data-testid={`col-details-${u.id}`}
        className="flex h-9 items-center justify-center rounded-md border border-blue-600 text-xs font-semibold text-blue-700 hover:bg-blue-50">
        See full details
      </Link>
    </div>
  );
}

function Cell({ c }) {
  if (c.yes) {
    return <span className="flex items-center gap-1 font-semibold text-green-700"><Check size={15} aria-hidden="true" /> Yes</span>;
  }
  if (c.no) {
    return <span className="flex items-center gap-1 text-gray-500"><X size={14} aria-hidden="true" /> No</span>;
  }
  if (c.missing) return <span className="text-[11px] italic text-gray-500">{c.text}</span>;
  return (
    <span className={`flex items-start justify-center gap-1 ${c.tick ? 'font-bold text-green-700' : 'text-gray-800'}`}>
      {c.tick && <Check size={15} className="mt-0.5 shrink-0" aria-label="Better" />}
      <span>{c.text}</span>
    </span>
  );
}

function Row({ row, cells }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-gray-100" data-testid={`row-${row.key}`}>
      <div className="flex items-center justify-between gap-2 px-2 pt-2.5">
        <p className="text-xs font-semibold text-gray-700 sm:text-sm">{row.label}</p>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="flex shrink-0 items-center gap-0.5 text-[11px] text-blue-700">
          What does this mean? <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </div>
      {open && <p className="px-2 pt-1 text-xs leading-relaxed text-gray-600">{row.means}</p>}
      <div className="grid gap-1 px-2 pb-2.5 pt-1.5" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
        {cells.map((c, i) => (
          <div key={i} className="flex justify-center text-center text-xs sm:text-sm"><Cell c={c} /></div>
        ))}
      </div>
    </div>
  );
}

function Section({ s, from, per, diffOnly }) {
  const [open, setOpen] = useState(true);
  const rows = diffOnly ? s.rows.filter((r) => r.differs) : s.rows;
  const hidden = s.rows.length - rows.length;
  return (
    <div className="border-t border-gray-200" data-testid={`section-${s.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between gap-2 bg-gray-50 px-3 py-2 text-left">
        <span className="text-sm font-bold text-gray-900">{s.title}</span>
        <span className="flex items-center gap-1 text-[11px] text-gray-500">
          {hidden > 0 && `${hidden} same, hidden · `}
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </span>
      </button>
      {open && rows.map((r) => <Row key={r.key} row={r} cells={r.cells.slice(from, from + per)} />)}
      {open && rows.length === 0 && <p className="px-3 py-2 text-xs text-gray-500">Same on every AC here.</p>}
    </div>
  );
}

export default function CompareTable({ units, onKit }) {
  const phone = useIsPhone();
  const per = phone ? 2 : units.length;
  const [start, setStart] = useState(0);
  const [diffOnly, setDiffOnly] = useState(false);
  const touch = useRef(null);
  const maxStart = Math.max(0, units.length - per);
  const from = Math.min(start, maxStart);
  const shown = units.slice(from, from + per);
  const sections = tableSections(units, COMPARE_CTX);

  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const d = e.changedTouches[0].clientX - touch.current;
    touch.current = null;
    if (d < -50) setStart((s) => Math.min(maxStart, s + 1));
    if (d > 50) setStart((s) => Math.max(0, s - 1));
  };

  if (!units.length) return null;
  const cols = `repeat(${shown.length}, minmax(0, 1fr))`;

  return (
    <section className="mt-8" data-testid="compare-table" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold text-gray-900 md:text-lg">Full comparison</h2>
        {units.length > 1 && (
          <button type="button" role="switch" aria-checked={diffOnly} onClick={() => setDiffOnly((d) => !d)} data-testid="diff-only"
            className="flex min-h-[40px] items-center gap-2 rounded-full border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-800 sm:text-sm">
            <span className={`relative inline-block h-5 w-9 rounded-full transition ${diffOnly ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${diffOnly ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
            Show only differences
          </button>
        )}
      </div>
      {phone && units.length > per && (
        <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
          <button type="button" disabled={from === 0} onClick={() => setStart((s) => Math.max(0, s - 1))} data-testid="table-prev"
            className="flex items-center gap-0.5 rounded-md border border-gray-300 px-2 py-1 disabled:opacity-30"><ChevronLeft size={14} /> Back</button>
          <span>Swipe for more · {from + 1}-{from + shown.length} of {units.length}</span>
          <button type="button" disabled={from >= maxStart} onClick={() => setStart((s) => Math.min(maxStart, s + 1))} data-testid="table-next"
            className="flex items-center gap-0.5 rounded-md border border-gray-300 px-2 py-1 disabled:opacity-30">Next <ChevronRight size={14} /></button>
        </div>
      )}
      <div className="mt-2 rounded-xl border border-gray-200 bg-white">
        <div className="sticky top-16 z-30 grid rounded-t-xl border-b border-gray-200 bg-white lg:top-20" style={{ gridTemplateColumns: cols }} data-testid="table-head">
          {shown.map((u) => <Head key={u.id} u={u} onKit={onKit} />)}
        </div>
        {sections.map((s) => <Section key={s.id} s={s} from={from} per={per} diffOnly={diffOnly} />)}
        <div className="grid border-t border-gray-200" style={{ gridTemplateColumns: cols }}>
          {shown.map((u) => <Foot key={u.id} u={u} />)}
        </div>
      </div>
    </section>
  );
}
